'use client';

/* eslint-disable max-lines, tailwindcss/classnames-order, tailwindcss/enforces-shorthand */

import { useIsMicTestLiveTranscriptionEnabled } from '@/features/flags';
import { useOnboardingContext } from '@/features/onboarding-v2/context/OnboardingContext';
import { useLocalizedSpecialtyName } from '@/features/onboarding-v2/hooks/useLocalizedSpecialtyName';
import { useSelectedSpecialtyCanonicalName } from '@/features/onboarding-v2/hooks/useSelectedSpecialtyCanonicalName';
import { useQuerySpecialtyOnboardingV2Assets } from '@/hooks/query/useQuerySpecialtyOnboardingV2Assets';
import { formatCjkAwareDisplayName } from '@/utils/i18n/cjkLocales';
import { Fade } from '@heidi/design-system/fade';
import { Evidence } from '@heidi/design-system/icons';
import {
  TypographyH3,
  TypographyP,
  TypographyP2,
  TypographyPBold,
} from '@heidi/design-system/typography';
import type {
  DetailLevel,
  EditActivity,
  EnhancedLine,
  MicTestPhase,
  TourPreviewTab,
  TranscriptLine,
} from '@heidi/growth';
import {
  DetailNoteBody,
  EnhancedContent,
  FALLBACK_SAMPLE_TEXT,
  MicListeningStatus,
  NoteSignature,
  parseExampleNoteIntoSections,
  ReferralContentRenderer,
  splitTranscriptIntoChunks,
  StatusPill,
  TourPreviewTabs,
  transcriptToDialogue,
  useLearnBasicsDocumentOptions,
} from '@heidi/growth';
import {
  ArrowRight,
  AudioLines,
  FileText,
  Info,
  Loader2,
  PenLine,
  X,
} from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { micTestPreviewMessages } from './mic-test-preview.messages';
import { EvidenceEmptyChat } from './variants/EvidenceEmptyChat';
import { EvidenceLayoutRenderer } from './variants/EvidenceLayouts';
import { MockEvidenceResponse } from './variants/MockEvidenceResponse';

const SUGGESTED_EVIDENCE_QUERY =
  'What are CSANZ chest pain guidelines for a 50 year old caucasian male?';

const ANALYZE_DURATION_MS = 2000;
const LINE_ENHANCE_INTERVAL_MS = 1200;
const GENERATION_FAILURE_HOLD_MS = 1500;
const LAYOUT_EASE = [0.4, 0, 0.2, 1] as const;

export type { EditActivity, MicTestPhase } from '@heidi/growth';

/**
 * Plan-like header names recognized as the evidence affordance target. The
 * Use Evidence affordance attaches to the LAST plan-like section it finds,
 * so a note with both "Assessment & Plan" and "Plan" lands the chip on
 * "Plan". When no match is found, the caller falls back to the last
 * section so headerless or non-SOAP notes still get an interaction surface.
 */
const PLAN_HEADER_NAMES = new Set([
  'plan',
  'plans',
  'management',
  'management plan',
  'treatment',
  'treatment plan',
  'recommendations',
  'plan and recommendations',
  'assessment and plan',
  'assessment & plan',
]);

function findEvidenceTargetIndex(sections: { header: string }[]): number {
  if (sections.length === 0) return -1;
  for (let i = sections.length - 1; i >= 0; i -= 1) {
    const header = sections[i]!.header.trim().toLowerCase();
    if (PLAN_HEADER_NAMES.has(header)) return i;
  }
  return sections.length - 1;
}

interface NoteSection {
  header: string;
  body: EnhancedLine[];
}

function buildSections(lines: TranscriptLine[]): NoteSection[] {
  const allEnhanced = lines.flatMap((tl) => tl.enhanced);
  const sections: NoteSection[] = [];
  for (const line of allEnhanced) {
    if (line.type === 'header') {
      sections.push({ header: line.text, body: [] });
    } else {
      // Synthesise an unheaded leading section so the first paragraph/bullet
      // doesn't get silently dropped on notes that open with content
      // (e.g. specialties whose seeded note begins mid-paragraph).
      if (sections.length === 0) {
        sections.push({ header: '', body: [] });
      }
      sections[sections.length - 1]!.body.push(line);
    }
  }
  return sections;
}

/**
 * Detail-level transform applied client-side during the Learn-the-basics
 * "Choose a detail level" task. The /onboarding-v2-assets note is a single
 * fixed note (no Low/Med/High variants), so we approximate the levels by
 * keeping fewer body lines per section for Low/Medium and all of them for
 * High. PRODUCT NOTE: this is presentation-only — a real implementation would
 * regenerate the note at the chosen verbosity server-side.
 */
function applyDetailLevel(
  sections: TranscriptLine[],
  level: DetailLevel
): TranscriptLine[] {
  if (level === 'high') return sections;
  return sections.map((tl) => {
    const headers = tl.enhanced.filter((l) => l.type === 'header');
    const body = tl.enhanced.filter((l) => l.type !== 'header');
    // Low keeps a single bullet; Medium keeps ~60% but ALWAYS at least one
    // fewer than High (capped at body.length - 1) so going Medium → High
    // visibly adds a bullet even for short 2-line sections.
    const keep =
      level === 'low'
        ? 1
        : Math.max(1, Math.min(body.length - 1, Math.ceil(body.length * 0.6)));
    return { ...tl, enhanced: [...headers, ...body.slice(0, keep)] };
  });
}

type CurrentTask = 'updatedStyle' | 'usedEvidence' | 'createdDocument' | null;

function EvidenceStartButton({ onStart }: { onStart: () => void }) {
  return (
    <m.div
      className="relative z-50 -mx-4 mt-2 rounded-2xl bg-surface px-4 py-4 shadow-md"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <button
        type="button"
        onClick={onStart}
        className="group flex w-full items-center gap-4 rounded-xl border border-accent-purple/20 bg-accent-purple/5 p-4 text-left transition-colors hover:border-accent-purple/40 hover:bg-accent-purple/10"
      >
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent-purple/10 text-accent-purple transition-colors group-hover:bg-accent-purple/20">
          <Evidence size={20} />
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-text-primary">
            <FormattedMessage
              defaultMessage="Use Evidence"
              id="3fRYkM8aC3"
              description="Title of the card that starts the Heidi Evidence demo step"
            />
          </span>
          <span className="text-xs text-text-tertiary">
            <FormattedMessage
              defaultMessage="Search clinical guidelines to strengthen your plan"
              id="TyIGBADGt7"
              description="Subtitle of the card that starts the Heidi Evidence demo step"
            />
          </span>
        </div>
        <ArrowRight className="ms-auto size-4 text-text-tertiary transition-transform group-hover:translate-x-0.5 rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5" />
      </button>
    </m.div>
  );
}

const EVIDENCE_SUGGESTIONS = [
  'Arrange appointment with dietitian or exercise physiologist for lifestyle modification support',
  'Review current medications for cardiovascular risk factor optimisation',
];

function EditableNoteContent({
  lines,
  onActivity,
  currentTask,
  onOpenEvidenceTab,
  onEvidenceQueried,
  onEvidenceAccepted,
  onEvidenceDiscarded,
}: {
  lines: TranscriptLine[];
  onActivity?: (activity: EditActivity) => void;
  currentTask: CurrentTask;
  onOpenEvidenceTab?: () => void;
  onEvidenceQueried?: (params: {
    queryType: 'suggested' | 'custom';
    queryLength: number;
  }) => void;
  onEvidenceAccepted?: (itemsCount: number) => void;
  onEvidenceDiscarded?: (itemsCount: number) => void;
}) {
  const sections = buildSections(lines);
  const evidenceTargetIndex = findEvidenceTargetIndex(sections);
  // Note content is read-only in the onboarding preview — the user advances
  // by interacting with Customize Style / Use Evidence / Generate Referral
  // affordances rather than editing the note text directly. The body is
  // rendered through `NoteSectionBody` to preserve bullet vs. paragraph vs.
  // nested-header distinctions instead of flattening everything into a
  // bullet list.
  const [evidenceQuery, setEvidenceQuery] = useState('');
  const [evidenceStarted, setEvidenceStarted] = useState(false);
  const [evidenceSubmitted, setEvidenceSubmitted] = useState(false);
  const [evidenceLines, setEvidenceLines] = useState<string[]>([]);
  const [evidencePhase, setEvidencePhase] = useState<
    'input' | 'animating' | 'approval' | 'done'
  >('input');
  const activityRef = useRef<EditActivity>({
    updatedStyle: false,
    usedEvidence: false,
    createdDocument: false,
  });
  const onActivityRef = useRef(onActivity);
  onActivityRef.current = onActivity;

  const handleEvidenceSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (evidenceSubmitted) return;
    const trimmedUserQuery = evidenceQuery.trim();
    const usedSuggestion = trimmedUserQuery === '';
    const query = usedSuggestion ? SUGGESTED_EVIDENCE_QUERY : trimmedUserQuery;
    if (usedSuggestion) {
      setEvidenceQuery(query);
    }
    onEvidenceQueried?.({
      queryType: usedSuggestion ? 'suggested' : 'custom',
      queryLength: query.length,
    });
    setEvidenceSubmitted(true);
    setEvidencePhase('done');
    onOpenEvidenceTab?.();
  };

  const handleEvidenceAccept = () => {
    setEvidencePhase('done');
    onEvidenceAccepted?.(EVIDENCE_SUGGESTIONS.length);
    setTimeout(() => {
      const activity = { ...activityRef.current, usedEvidence: true };
      activityRef.current = activity;
      onActivityRef.current?.(activity);
    }, 1000);
  };

  const isEvidenceStep = currentTask === 'usedEvidence';

  return (
    <div className="flex flex-1 flex-col gap-4">
      {sections.map((section, i) => {
        // Resolved once outside the loop so both the in-place evidence overlay
        // and the EvidenceStartButton attach to the same section even when a
        // specialty's note doesn't include a literal "Plan" header.
        const isPlan = i === evidenceTargetIndex;

        return (
          <div
            key={`section-${i}-${section.header}`}
            className="flex flex-col gap-1 rounded-xl transition-all"
          >
            {section.header && <TypographyPBold>{section.header}</TypographyPBold>}
            {
              <>
                <DetailNoteBody body={section.body} />
                {isPlan &&
                  (evidencePhase === 'animating' ||
                    evidencePhase === 'approval' ||
                    evidenceLines.length > 0) && (
                    <m.div
                      className="-mt-3 flex flex-col"
                      layout
                      transition={{ layout: { duration: 0.7, ease: LAYOUT_EASE } }}
                    >
                      {evidenceLines.map((line, li) => (
                        <m.div
                          key={`evidence-${li}`}
                          layout
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{
                            layout: { duration: 0.7, ease: LAYOUT_EASE },
                            opacity: { duration: 0.6, ease: 'easeOut' },
                          }}
                        >
                          <TypographyP2
                            className={
                              evidencePhase === 'done'
                                ? 'text-text-secondary'
                                : 'text-accent-forest'
                            }
                          >
                            &bull; {line}
                          </TypographyP2>
                        </m.div>
                      ))}

                      <AnimatePresence>
                        {evidencePhase === 'animating' && (
                          <m.div
                            layoutId="evidence-pill"
                            className="my-1 flex w-full items-center gap-2.5 rounded-xl bg-accent-forest/10 px-4 py-2.5"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                              layout: { duration: 0.7, ease: LAYOUT_EASE },
                              opacity: { duration: 0.2 },
                            }}
                          >
                            <Loader2 className="size-4 animate-spin text-accent-forest" />
                            <TypographyPBold className="text-accent-forest">
                              <FormattedMessage
                                defaultMessage="Heidi Evidence updating your note"
                                id="N60ZaSLcis"
                                description="Status pill while Evidence suggestions are being merged into the demo note"
                              />
                            </TypographyPBold>
                          </m.div>
                        )}
                      </AnimatePresence>
                      {evidencePhase === 'approval' && (
                        <m.div
                          className="mt-2 flex items-center gap-2 border-t border-dashed border-accent-forest/20 pt-3"
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <button
                            type="button"
                            onClick={handleEvidenceAccept}
                            className="rounded-lg bg-accent-forest px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-forest/90"
                          >
                            <FormattedMessage
                              defaultMessage="Save changes"
                              id="8q5L3dQNjp"
                              description="Button that accepts the Evidence suggestions into the demo note"
                            />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              onEvidenceDiscarded?.(EVIDENCE_SUGGESTIONS.length);
                              setEvidenceLines([]);
                              setEvidencePhase('done');
                              setTimeout(() => {
                                const activity2 = {
                                  ...activityRef.current,
                                  usedEvidence: true,
                                };
                                activityRef.current = activity2;
                                onActivityRef.current?.(activity2);
                              }, 1000);
                            }}
                            className="rounded-lg border border-border px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-accent"
                          >
                            <FormattedMessage
                              defaultMessage="Discard"
                              id="F+uWNydA3x"
                              description="Button that rejects the Evidence suggestions in the demo note"
                            />
                          </button>
                          <span className="ml-auto text-[10px] text-text-tertiary">
                            {/* Descriptor lives in the messages module so the
                                ICU plural is unit-testable (renderWithIntl
                                swallows ICU errors via onError). */}
                            <FormattedMessage
                              {...micTestPreviewMessages.itemsFromGuidelines}
                              values={{ count: EVIDENCE_SUGGESTIONS.length }}
                            />
                          </span>
                        </m.div>
                      )}
                    </m.div>
                  )}
              </>
            }
            {isPlan &&
              isEvidenceStep &&
              evidencePhase === 'input' &&
              !evidenceStarted && (
                <EvidenceStartButton onStart={() => setEvidenceStarted(true)} />
              )}
            {isPlan && isEvidenceStep && evidencePhase === 'input' && evidenceStarted && (
              <m.div
                className="relative z-50 -mx-4 mt-2 rounded-2xl bg-surface px-4 py-3 shadow-md"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <EvidenceLayoutRenderer
                  query={evidenceQuery}
                  onQueryChange={setEvidenceQuery}
                  onSubmit={() => handleEvidenceSubmit()}
                  submitted={evidenceSubmitted}
                  loading={false}
                />
              </m.div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function MicTestPreview({
  phase,
  transcript,
  onPhaseChange,
  onEditActivity,
  recordingDurationSec = 0,
  isGuidedTour = false,
  isTourEvidenceStep = false,
}: {
  phase: MicTestPhase;
  transcript: string;
  onPhaseChange: (phase: MicTestPhase) => void;
  onEditActivity?: (activity: EditActivity) => void;
  recordingDurationSec?: number;
  /**
   * Guided-tour arm. The pane already carries a photo backdrop (so the card's
   * own tinted field would sit on top of it as a wash) and the frames drop the
   * card header in favour of a floating tab strip above the card.
   */
  isGuidedTour?: boolean;
  /**
   * The tour's evidence screen. The Evidence tab is the point of that screen,
   * so it joins the strip on arrival (empty chat) rather than waiting for the
   * first question.
   */
  isTourEvidenceStep?: boolean;
}) {
  const intl = useIntl();
  const { formData, micTest: micTestCtx, analytics } = useOnboardingContext();
  const previewDisplayName = formatCjkAwareDisplayName(
    {
      firstName:
        formData.firstName || intl.formatMessage(micTestPreviewMessages.defaultFirstName),
      lastName:
        formData.lastName || intl.formatMessage(micTestPreviewMessages.defaultLastName),
    },
    formData.displayLanguage
  );

  // Canonical (English) specialty name for the asset lookup — the BE resolves by
  // English name, so don't send the now-localized picker value. TanStack Query
  // dedupes against the same query key used in `LearnBasicsFlowSteps`, so this is
  // a free read.
  const specialtyAssetName = useSelectedSpecialtyCanonicalName();
  // `isPending` stays true until the query resolves and flips false on a hard
  // error, so the editing-phase pill below cannot spin forever on failure.
  const { data: onboardingAssets, isPending: isAssetsPending } =
    useQuerySpecialtyOnboardingV2Assets(specialtyAssetName, intl.locale);

  // The signature shows the specialty in the active display language (shared
  // with the personalised mic-test heading — see useLocalizedSpecialtyName).
  const localizedSpecialtyName = useLocalizedSpecialtyName();
  const liveTranscriptionEnabled = useIsMicTestLiveTranscriptionEnabled();

  // During the mic-test reading phase the preview shows the script the user
  // reads ("You'll be saying"), highlighting each word as the simulated
  // transcription reveals it. The spoken `transcript` is a growing prefix of
  // this sample, so the revealed count is just its word count.
  const sampleScript = onboardingAssets?.transcript || FALLBACK_SAMPLE_TEXT;

  // Speaker-turn render in both layouts, so the transcript's English
  // `Clinician:` / `Patient:` tags become localised labels.
  const dialogueSegments = useMemo(
    () => transcriptToDialogue(sampleScript),
    [sampleScript]
  );
  // Flat word list (speaker labels excluded) that the reveal counts over —
  // its order matches the turns' concatenation so `revealedWordCount` aligns.
  const sampleScriptWords = useMemo(
    () => dialogueSegments.flatMap((s) => s.words),
    [dialogueSegments]
  );
  // How many trailing revealed words stay green before settling to plain text.
  const WORD_HIGHLIGHT_WINDOW = 4;
  // Clamped: skip/continue publish the raw transcript, whose word count
  // exceeds the label-free sample list.
  const revealedWordCount = useMemo(() => {
    const trimmed = transcript.trim();
    const count = trimmed ? trimmed.split(/\s+/).filter(Boolean).length : 0;
    return Math.min(count, sampleScriptWords.length);
  }, [transcript, sampleScriptWords.length]);
  const isTranscriptComplete =
    sampleScriptWords.length > 0 && revealedWordCount >= sampleScriptWords.length;

  // Stepped Learn-the-basics: the left rail owns the step controls, so the
  // preview no longer spotlights/dims a single task. `currentTask` is kept as a
  // constant `null` so the (shared) note/evidence/document rendering reads as
  // "nothing highlighted".
  const currentTask: CurrentTask = null;

  // Stable setters from context (destructured so effect deps don't churn).
  const {
    setNoteTab: setActiveTab,
    setDetailLevel: setCtxDetailLevel,
    setEvidenceRequested: setCtxEvidenceRequested,
    setEvidenceQueryIndex: setCtxEvidenceQueryIndex,
    setSelectedDocuments: setCtxSelectedDocuments,
  } = micTestCtx;
  // Active tab + selected documents are shared with the left rail so the tab
  // strip stays in sync (multi-select Documents step opens one tab per doc).
  const activeTab = micTestCtx.noteTab;
  const selectedDocuments = micTestCtx.selectedDocuments;
  const [showEvidenceTab, setShowEvidenceTab] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  // Detail level is owned by the left rail (shared via context).
  const detailLevel = micTestCtx.detailLevel;

  // The left rail requests opening the Evidence tab; mirror it into local tab
  // state once and focus it. (Document tabs are driven directly by the
  // `selectedDocuments` list — the left sets `noteTab` to `doc-${index}`.)
  // On the tour's evidence screen the tab is present from the start, so the
  // strip shows the full trail (transcript → note → documents → evidence)
  // while the chat is still empty.
  const { evidenceRequested } = micTestCtx;
  useEffect(() => {
    if (
      phase === 'editing' &&
      (evidenceRequested || isTourEvidenceStep) &&
      !showEvidenceTab
    ) {
      setShowEvidenceTab(true);
      setActiveTab('evidence');
    }
  }, [phase, evidenceRequested, isTourEvidenceStep, showEvidenceTab, setActiveTab]);
  const activitySnapshotRef = useRef<EditActivity | null>(null);
  const [stage, setStage] = useState<'idle' | 'analyzing' | 'creating' | 'done'>('idle');
  const [enhancedCount, setEnhancedCount] = useState(0);
  const [generatedSections, setGeneratedSections] = useState<EnhancedLine[][] | null>(
    null
  );
  const [generationFailed, setGenerationFailed] = useState(false);
  const [analyzeMinElapsed, setAnalyzeMinElapsed] = useState(false);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onPhaseChangeRef = useRef(onPhaseChange);
  onPhaseChangeRef.current = onPhaseChange;
  const contentRef = useRef<HTMLDivElement>(null);

  // Per-specialty content for every preview surface (note, abbreviations,
  // document). `onboardingAssets` is fetched once near the top of the
  // component; the memos below derive parsed views from it.
  const apiNoteSections = useMemo<EnhancedLine[][]>(() => {
    if (!onboardingAssets?.note) return [];
    return parseExampleNoteIntoSections(onboardingAssets.note);
  }, [onboardingAssets?.note]);

  // Reset every piece of preview state when the phase returns to 'recording'
  // (e.g. user clicked Back from note-ready/note-edit). Without this, going
  // back would leave stale generation state visible until the next Continue.
  useEffect(() => {
    if (phase !== 'recording') return;
    setStage('idle');
    setEnhancedCount(0);
    setGeneratedSections(null);
    setGenerationFailed(false);
    setAnalyzeMinElapsed(false);
    // Editing-phase UI surfaces (panels, secondary tabs, abbreviation
    // overlays) shouldn't survive a Back, otherwise re-entering edit shows
    // half-open style panels or already-abbreviated notes.
    setActiveTab('note');
    setShowEvidenceTab(false);
    setIsRegenerating(false);
    setRegenSectionCount(99);
    setCtxDetailLevel('medium');
    setCtxEvidenceRequested(false);
    setCtxEvidenceQueryIndex(0);
    setCtxSelectedDocuments([]);
    activitySnapshotRef.current = null;
    // The note, abbreviations, and document body all come synchronously
    // from /onboarding-v2-assets — no in-flight mutations to discard.
  }, [
    phase,
    setActiveTab,
    setCtxDetailLevel,
    setCtxEvidenceRequested,
    setCtxEvidenceQueryIndex,
    setCtxSelectedDocuments,
  ]);

  // Drive the analyze→creating animation off the pre-known specialty note.
  // Holds for ANALYZE_DURATION_MS so the user reads "Analyzing transcript..."
  // even though the data is already in memory; downstream effects swap to
  // 'creating' once the timer completes and the sections are populated.
  useEffect(() => {
    if (phase !== 'generating') return;
    setStage('analyzing');
    setAnalyzeMinElapsed(false);
    setGeneratedSections(null);
    setGenerationFailed(false);
    setEnhancedCount(0);

    const minTimer = setTimeout(() => setAnalyzeMinElapsed(true), ANALYZE_DURATION_MS);

    if (!transcript.trim()) {
      setGenerationFailed(true);
      return () => clearTimeout(minTimer);
    }

    if (apiNoteSections.length > 0) {
      setGeneratedSections(apiNoteSections);
    } else {
      // Onboarding-v2-assets hasn't resolved yet. The downstream `creating`
      // effect waits on `generatedSections !== null || generationFailed`, so
      // leaving it null here just stretches the analyzing pill until the
      // hook lands. Failure is only flagged for empty transcripts.
    }

    return () => clearTimeout(minTimer);
  }, [phase, transcript, apiNoteSections]);

  // Late arrival: if the specialty assets resolve after the generating effect
  // already fired, push the sections in. Cheap to re-run; setGeneratedSections
  // is a no-op when the value is reference-equal.
  useEffect(() => {
    if (phase !== 'generating') return;
    if (apiNoteSections.length === 0) return;
    setGeneratedSections((prev) => prev ?? apiNoteSections);
  }, [phase, apiNoteSections]);

  // Per-specialty document body, parsed once and passed straight to the
  // referral tab. Synchronous — no LLM round trip — so we don't need a
  // separate fetch-state copy; while the assets are still loading
  // `documentSections` is `[]` and the renderer just shows the pill.
  const documentSections = useMemo<EnhancedLine[][]>(() => {
    const primaryBody = onboardingAssets?.documents?.[0]?.body;
    if (!primaryBody) return [];
    return parseExampleNoteIntoSections(primaryBody);
  }, [onboardingAssets?.documents]);

  // The three per-specialty recommended documents (type + body from the API).
  // Each checked option opens its own Document tab.
  const documentOptions = useLearnBasicsDocumentOptions(onboardingAssets?.documents);

  const handleDone = useCallback(() => {
    setStage('done');
    onPhaseChangeRef.current('finished');
  }, []);

  useEffect(() => {
    if (stage !== 'analyzing') return;
    if (!analyzeMinElapsed) return;
    if (generatedSections === null && !generationFailed) return;
    setStage('creating');
  }, [stage, analyzeMinElapsed, generatedSections, generationFailed]);

  useEffect(() => {
    if (stage !== 'creating') return;

    // Failure path: hold the "Creating note" pill for a beat, then advance.
    if (generationFailed || (generatedSections?.length ?? 0) === 0) {
      timerRef.current = setTimeout(handleDone, GENERATION_FAILURE_HOLD_MS);
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }

    const totalSections = generatedSections!.length;
    let count = 0;
    intervalRef.current = setInterval(() => {
      count += 1;
      setEnhancedCount(count);
      if (count >= totalSections) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        timerRef.current = setTimeout(handleDone, 600);
      }
    }, LINE_ENHANCE_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [stage, generatedSections, generationFailed, handleDone]);

  const isActive = stage !== 'idle';
  const isEditing = phase === 'editing';
  const showPill = stage === 'analyzing' || stage === 'creating';
  const pillLabel =
    stage === 'analyzing'
      ? intl.formatMessage(micTestPreviewMessages.analyzingTranscript)
      : intl.formatMessage(micTestPreviewMessages.creatingNote);
  const isRecordingPhase = phase === 'recording';
  const showSignature = isRecordingPhase || isActive || isEditing;

  // Guided-tour arm: the tab strip floats above the card and accumulates one
  // pill per artefact the tour has produced, so the transcript stays reachable
  // for the whole flow. Labels reuse the control arm's strings — the tour adds
  // no new copy.
  const tourTabs = useMemo<TourPreviewTab[]>(() => {
    if (!isGuidedTour) return [];
    const tabs: TourPreviewTab[] = [
      {
        id: 'transcript',
        label: intl.formatMessage(micTestPreviewMessages.transcript),
        icon: <AudioLines className="size-3 text-accent-forest" />,
      },
    ];
    if (isEditing) {
      tabs.push({
        id: 'note',
        label: intl.formatMessage(micTestPreviewMessages.clinicalNote),
        icon: <PenLine className="size-3 text-accent-pink" />,
      });
      for (const docIndex of selectedDocuments) {
        tabs.push({
          id: `doc-${docIndex}`,
          label:
            documentOptions[docIndex]?.name ||
            intl.formatMessage(micTestPreviewMessages.referralLetter),
          icon: <FileText className="size-3 text-accent-red" />,
        });
      }
      if (showEvidenceTab) {
        tabs.push({
          id: 'evidence',
          label: intl.formatMessage(micTestPreviewMessages.evidence),
          icon: <Evidence size={12} className="text-accent-purple" />,
        });
      }
    }
    return tabs;
  }, [
    isGuidedTour,
    isEditing,
    selectedDocuments,
    documentOptions,
    showEvidenceTab,
    intl,
  ]);
  // Before the note exists the transcript is the only thing on screen, so the
  // strip shows it as selected even though `noteTab` still holds its default.
  const tourActiveTab = isEditing ? activeTab : 'transcript';
  const showTranscriptBody =
    (stage === 'idle' && !isEditing) ||
    (isGuidedTour && isEditing && activeTab === 'transcript');

  // Adapt API-generated sections to the `TranscriptLine[]` shape consumed by
  // the editable-note view and the regenerate animation. Reads the parsed
  // API note directly when the generating animation never ran (skipped
  // mic test, restored progress), so the note is always the localized
  // per-specialty content, never placeholder copy.
  const editableNoteSections = useMemo<TranscriptLine[]>(() => {
    const sections = generatedSections?.length ? generatedSections : apiNoteSections;
    return sections.map((enhanced) => ({ raw: '', enhanced }));
  }, [generatedSections, apiNoteSections]);

  // Pair the per-specialty transcript with the parsed note sections so
  // the analyzing → creating reveal can mirror the original Learn-the-
  // Basics demo: enhanced sections build up at the top while the
  // remaining raw transcript shrinks below the loading pill, sentence
  // by sentence. Before the API note resolves only the raw transcript
  // chunks render (dimmed, below the pill), with no placeholder note.
  const transcriptToNotePairs = useMemo<TranscriptLine[]>(() => {
    const chunks = splitTranscriptIntoChunks(transcript);
    if (!generatedSections || generatedSections.length === 0) {
      return chunks.map((raw) => ({ raw, enhanced: [] }));
    }
    const sectionCount = generatedSections.length;
    const chunkCount = chunks.length;
    return generatedSections.map((enhanced, sectionIndex) => {
      // Distribute transcript chunks evenly across note-section ticks.
      // When there are fewer chunks than sections (typical: 2-sentence
      // transcript, 5-section note) some pairs end up with `raw === ''`
      // — the renderer skips empty chunks so the bottom area just
      // shrinks earlier than the top finishes.
      if (chunkCount === 0) return { raw: '', enhanced };
      const start = Math.floor((sectionIndex * chunkCount) / sectionCount);
      const end = Math.floor(((sectionIndex + 1) * chunkCount) / sectionCount);
      const raw = chunks.slice(start, end).join(' ');
      return { raw, enhanced };
    });
  }, [generatedSections, transcript]);

  // Detail-level view of the note: a client-side transform of the base note
  // sections (fewer bullets for Low/Medium, all for High). See PRODUCT NOTE on
  // `applyDetailLevel` — there are no server-side verbosity variants today.
  const detailedNoteSections = useMemo<TranscriptLine[]>(
    () => applyDetailLevel(editableNoteSections, detailLevel),
    [editableNoteSections, detailLevel]
  );

  useEffect(() => {
    if (!isRegenerating) return;
    setRegenSectionCount(0);
    const totalSections = editableNoteSections.length;
    let count = 0;
    const id = setInterval(() => {
      count += 1;
      setRegenSectionCount(count);
      if (count >= totalSections) {
        clearInterval(id);
        setTimeout(() => {
          setIsRegenerating(false);
        }, 300);
      }
    }, 600);
    return () => clearInterval(id);
  }, [isRegenerating, micTestCtx.editActivity, onEditActivity, editableNoteSections]);

  const [regenSectionCount, setRegenSectionCount] = useState(99);

  useEffect(() => {
    if (!isEditing || !currentTask) return;
    const el = contentRef.current;
    if (!el) return;

    // ResizeObserver on the scroll child avoids MutationObserver subtree thrash.
    const target = el.firstElementChild;
    if (!target) return;

    let rafId = 0;
    const scrollToBottom = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    };

    scrollToBottom();
    const observer = new ResizeObserver(scrollToBottom);
    observer.observe(target);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [isEditing, currentTask]);

  return (
    <div
      className={`relative flex size-full flex-col overflow-hidden ${
        isGuidedTour
          ? // The tour frames inset the card 64px from every pane edge (at the
            // 696px-wide pane they're drawn at) and sit the tab strip in that
            // top gutter. Stepping the inset down on narrower panes keeps the
            // same proportion rather than squeezing the card.
            'gap-2.5 p-8 xl:p-12 2xl:p-16'
          : 'bg-accent-sunlight/20 p-6 pl-4'
      }`}
      style={liveTranscriptionEnabled ? { perspective: '2000px' } : undefined}
    >
      {isGuidedTour && (
        <TourPreviewTabs
          tabs={tourTabs}
          activeTab={tourActiveTab}
          onSelect={setActiveTab}
        />
      )}
      <m.div
        className="relative z-[31] flex min-h-0 w-full flex-1 flex-col rounded-2xl bg-surface shadow-lg"
        style={
          liveTranscriptionEnabled
            ? { transformOrigin: 'center', backfaceVisibility: 'hidden' }
            : undefined
        }
        // Option-6 / V2: the panel flips in like a card turning over to reveal
        // the live transcript when entering the mic-test step.
        initial={liveTranscriptionEnabled ? { rotateY: -90, opacity: 0 } : false}
        animate={liveTranscriptionEnabled ? { rotateY: 0, opacity: 1 } : undefined}
        transition={{ duration: 0.55, ease: [0.215, 0.61, 0.355, 1] }}
      >
        {/* Stepped Learn-the-basics: no spotlight overlay — the note stays fully
            visible while the left rail drives detail / evidence / document. */}

        {/* The guided-tour frames drop the card header entirely — the floating
            tab strip above the card carries the labelling instead, and the
            reclaimed height goes to the note. */}
        {!isGuidedTour && (
          <div className="flex shrink-0 flex-col border-b border-border">
            <div
              className={`px-6 py-4 transition-all ${
                isEditing && currentTask ? 'opacity-15' : ''
              }`}
            >
              {/* "You'll be saying" labels the script the user reads aloud during
                the record/transcription (recording) phase. Once recording ends,
                the panel shows the structured note being generated / ready /
                edited, so the title switches to "Clinical Note" from the
                "your first note is ready" screen onward. (Originally this only
                flipped on edit mode — Jacob Tait's Learn-the-Basics, #9177.) */}
              {liveTranscriptionEnabled && !isEditing ? (
                // Option-6 layout: a single row — Listening waveform · Live
                // transcript title · word counter (no name/duration subline).
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <MicListeningStatus
                      active={isRecordingPhase}
                      label={
                        isRecordingPhase
                          ? intl.formatMessage(micTestPreviewMessages.transcribing)
                          : intl.formatMessage(micTestPreviewMessages.transcribed)
                      }
                    />
                    {!isRecordingPhase && (
                      <TypographyH3>
                        <FormattedMessage {...micTestPreviewMessages.clinicalNote} />
                      </TypographyH3>
                    )}
                  </div>
                  <TypographyP2 className="shrink-0 font-mono tabular-nums text-text-tertiary">
                    {revealedWordCount} / {sampleScriptWords.length}
                  </TypographyP2>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <TypographyH3>
                      {isRecordingPhase ? (
                        <FormattedMessage {...micTestPreviewMessages.youllBeSaying} />
                      ) : (
                        <FormattedMessage {...micTestPreviewMessages.clinicalNote} />
                      )}
                    </TypographyH3>
                    <TypographyP2 className="mt-1 text-text-tertiary">
                      {previewDisplayName} {formatDuration(recordingDurationSec)}
                    </TypographyP2>
                  </div>
                  {!isEditing && (
                    <TypographyP2 className="shrink-0 font-mono tabular-nums text-text-tertiary">
                      {revealedWordCount} / {sampleScriptWords.length}
                    </TypographyP2>
                  )}
                </div>
              )}
            </div>

            {isEditing && (
              <div className="px-6 pb-3">
                <div className="flex items-center gap-1 overflow-x-auto">
                  <button
                    type="button"
                    data-state={activeTab === 'note' ? 'active' : 'unchecked'}
                    onClick={() => setActiveTab('note')}
                    className="group flex shrink-0 items-center gap-x-1 whitespace-nowrap rounded-md border border-transparent px-2 py-1 text-sm text-text-secondary hover:border-outline data-[state=active]:border-outline data-[state=active]:text-text-primary"
                  >
                    <PenLine className="size-4 text-text-secondary group-hover:text-accent-pink group-data-[state=active]:text-accent-pink" />
                    <FormattedMessage {...micTestPreviewMessages.clinicalNote} />
                  </button>
                  {selectedDocuments.map((docIndex) => {
                    const tabId = `doc-${docIndex}` as const;
                    const option = documentOptions[docIndex];
                    return (
                      <m.div
                        key={tabId}
                        className="flex shrink-0 items-center gap-0.5"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.15 }}
                      >
                        <button
                          type="button"
                          data-state={activeTab === tabId ? 'active' : 'unchecked'}
                          onClick={() => setActiveTab(tabId)}
                          className="group flex shrink-0 items-center gap-x-1 whitespace-nowrap rounded-md border border-transparent px-2 py-1 text-sm text-text-secondary hover:border-outline data-[state=active]:border-outline data-[state=active]:text-text-primary"
                        >
                          <FileText className="size-4 text-text-secondary group-hover:text-accent-red group-data-[state=active]:text-accent-red" />
                          {option?.name ||
                            intl.formatMessage(micTestPreviewMessages.referralLetter)}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setCtxSelectedDocuments(
                              selectedDocuments.filter((i) => i !== docIndex)
                            );
                            if (activeTab === tabId) setActiveTab('note');
                          }}
                          className="rounded p-0.5 text-text-tertiary hover:text-text-secondary"
                          aria-label={intl.formatMessage(
                            micTestPreviewMessages.closeReferralLetter
                          )}
                        >
                          <X className="size-3" />
                        </button>
                      </m.div>
                    );
                  })}
                  {showEvidenceTab && (
                    <m.div
                      className="flex shrink-0 items-center gap-0.5"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.15 }}
                    >
                      <button
                        type="button"
                        data-state={activeTab === 'evidence' ? 'active' : 'unchecked'}
                        onClick={() => setActiveTab('evidence')}
                        className="group flex shrink-0 items-center gap-x-1 whitespace-nowrap rounded-md border border-transparent px-2 py-1 text-sm text-text-secondary hover:border-outline data-[state=active]:border-outline data-[state=active]:text-text-primary"
                      >
                        <Evidence
                          size={16}
                          className="text-text-secondary group-hover:text-accent-purple group-data-[state=active]:text-accent-purple"
                        />
                        {/* Generic label — the Evidence tab must not claim a */}
                        {/* cardiac/CSANZ topic over a non-cardiac note. */}
                        <FormattedMessage {...micTestPreviewMessages.evidence} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowEvidenceTab(false);
                          setActiveTab('note');
                        }}
                        className="rounded p-0.5 text-text-tertiary hover:text-text-secondary"
                        aria-label={intl.formatMessage(
                          micTestPreviewMessages.closeEvidence
                        )}
                      >
                        <X className="size-3" />
                      </button>
                    </m.div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div
          ref={contentRef}
          className={`flex min-h-0 flex-1 flex-col overflow-y-auto px-6 ${
            isGuidedTour ? 'pb-0 pt-6' : 'py-4'
          }`}
        >
          {showTranscriptBody ? (
            <div className="flex flex-col gap-4">
              {dialogueSegments
                // Read-aloud shows the full script; live builds up turn by turn.
                .filter(
                  (seg) => !liveTranscriptionEnabled || seg.startIndex < revealedWordCount
                )
                .map((seg, si) => (
                  <div key={si}>
                    {seg.speaker !== 'narration' && (
                      <span
                        className={`block text-xs font-semibold uppercase tracking-wide text-text-tertiary ${
                          isGuidedTour ? 'mb-2' : 'mb-1'
                        }`}
                      >
                        {seg.speaker === 'clinician' ? (
                          <FormattedMessage
                            {...micTestPreviewMessages.clinicianSpeaker}
                          />
                        ) : (
                          <FormattedMessage {...micTestPreviewMessages.patientSpeaker} />
                        )}
                      </span>
                    )}
                    <TypographyP
                      className={`${
                        // The tour frames set the transcript at 14px/1.5 —
                        // noticeably tighter than the control arm's reading
                        // size, so the card holds the conversation without
                        // filling the pane.
                        isGuidedTour
                          ? 'text-[14px] leading-normal'
                          : seg.speaker === 'narration'
                            ? 'text-[15px] leading-[1.8]'
                            : 'text-[17px] leading-[1.8]'
                      } ${
                        seg.speaker === 'narration'
                          ? 'italic text-text-tertiary'
                          : 'text-text-primary'
                      }`}
                    >
                      {seg.words.map((word, wi) => {
                        const globalIndex = seg.startIndex + wi;
                        // Live hides unspoken words; read-aloud keeps all visible.
                        if (liveTranscriptionEnabled && globalIndex >= revealedWordCount)
                          return null;
                        // The leading edge lands green, then settles to plain
                        // text as later words arrive. Stage directions aren't
                        // speech, so they type in without the highlight.
                        const isFreshlySpoken =
                          !isTranscriptComplete &&
                          seg.speaker !== 'narration' &&
                          globalIndex < revealedWordCount &&
                          globalIndex >= revealedWordCount - WORD_HIGHLIGHT_WINDOW;
                        return (
                          <span
                            key={wi}
                            className={
                              isFreshlySpoken
                                ? 'rounded-sm bg-validation-success-muted text-validation-success transition-colors duration-500'
                                : 'transition-colors duration-500'
                            }
                          >
                            {word}{' '}
                          </span>
                        );
                      })}
                    </TypographyP>
                  </div>
                ))}
            </div>
          ) : null}

          {isEditing && activeTab === 'note' && isRegenerating && (
            <m.div
              className="flex flex-col gap-4"
              layout
              transition={{ layout: { duration: 0.7, ease: LAYOUT_EASE } }}
            >
              {editableNoteSections.slice(0, regenSectionCount).map((tl, i) => (
                <m.div
                  key={`regen-${i}`}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    layout: { duration: 0.7, ease: LAYOUT_EASE },
                    opacity: { duration: 0.6, ease: 'easeOut' },
                  }}
                >
                  <EnhancedContent lines={tl.enhanced} />
                </m.div>
              ))}
              <AnimatePresence>
                {regenSectionCount < editableNoteSections.length && (
                  <StatusPill
                    label={intl.formatMessage(
                      micTestPreviewMessages.addingDetailFromTranscript
                    )}
                  />
                )}
              </AnimatePresence>
            </m.div>
          )}

          {isEditing &&
            activeTab === 'note' &&
            !isRegenerating &&
            (editableNoteSections.length === 0 && isAssetsPending ? (
              // Entered editing before /onboarding-v2-assets resolved (skipped
              // mic test, restored progress): hold the creating-note pill
              // instead of flashing an empty card until the sections arrive.
              <StatusPill
                label={intl.formatMessage(micTestPreviewMessages.creatingNote)}
              />
            ) : (
              // The stepped left rail drives the note now (the old spotlight/dim
              // overlay is gone), so the note stays fully visible — otherwise the
              // detail-change highlight would be dimmed and unreadable.
              <m.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <EditableNoteContent
                  // No detail-level key remount: DetailNoteBody diffs the new
                  // sections against the previous render and highlights only the
                  // delta (added/removed bullets), so the note transforms in
                  // place rather than re-mounting wholesale.
                  lines={detailedNoteSections}
                  currentTask={currentTask}
                  onActivity={(activity) => {
                    activitySnapshotRef.current = activity;
                    onEditActivity?.(activity);
                  }}
                  onOpenEvidenceTab={() => {
                    setShowEvidenceTab(true);
                    setActiveTab('evidence');
                  }}
                  // The callback param is the PII-safe `{queryType, queryLength}`
                  // shape produced by `EditableNoteContent` — never the raw user
                  // query string. See `useOnboardingAnalytics.trackEvidenceQueried`.
                  onEvidenceQueried={(params) => analytics.trackEvidenceQueried(params)}
                  onEvidenceAccepted={(count) => analytics.trackEvidenceAccepted(count)}
                  onEvidenceDiscarded={(count) => analytics.trackEvidenceDiscarded(count)}
                />
              </m.div>
            ))}

          {isEditing &&
            selectedDocuments.map((docIndex) => {
              const option = documentOptions[docIndex];
              if (!option) return null;
              return (
                // Each selected document is its own persistent instance keyed by
                // its stable option index — switching tabs hides/shows via CSS
                // (no remount, no restart), so generations never stack or
                // duplicate. `createdDocument` is marked optimistically on the
                // left, so no onComplete wiring is needed here.
                <ReferralContentRenderer
                  key={docIndex}
                  visible={activeTab === `doc-${docIndex}`}
                  sections={option.sections ?? documentSections}
                  documentType={option.name}
                  firstName={formData.firstName}
                  lastName={formData.lastName}
                  specialtyName={localizedSpecialtyName}
                  displayLanguage={formData.displayLanguage}
                />
              );
            })}

          {isEditing && showEvidenceTab && !evidenceRequested && (
            // Tour evidence screen: the tab opens on arrival, so it waits here
            // until the clinician picks a question.
            <EvidenceEmptyChat visible={activeTab === 'evidence'} />
          )}

          {isEditing && showEvidenceTab && evidenceRequested && (
            // Single persistent instance: the response resets/replays on
            // `queryIndex` change (no key remount), and hides via CSS when the
            // tab isn't active — so switching tabs never duplicates content.
            <MockEvidenceResponse
              queryIndex={micTestCtx.evidenceQueryIndex}
              visible={activeTab === 'evidence'}
              onComplete={() => {
                setTimeout(() => {
                  const activity = { ...micTestCtx.editActivity, usedEvidence: true };
                  onEditActivity?.(activity);
                }, 200);
              }}
            />
          )}

          {isActive && !isEditing && (
            <m.div
              className="flex flex-col gap-4"
              layout
              transition={{ layout: { duration: 0.7, ease: LAYOUT_EASE } }}
            >
              {/* Top: enhanced note sections built up so far. Each tick of
                  the `creating` interval moves one pair from the
                  raw-transcript area below into this list — the original
                  Learn-the-Basics demo (Jacob Tait, #9177) used this
                  shape with hand-curated paired data, and we re-derive
                  the same pairing from `/onboarding-v2-assets` content
                  via `transcriptToNotePairs`. */}
              {transcriptToNotePairs.slice(0, enhancedCount).map((pair, i) =>
                pair.enhanced.length > 0 ? (
                  <m.div
                    key={`enhanced-${i}`}
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      layout: { duration: 0.7, ease: LAYOUT_EASE },
                      opacity: { duration: 0.6, ease: 'easeOut' },
                    }}
                  >
                    <EnhancedContent lines={pair.enhanced} />
                  </m.div>
                ) : null
              )}

              <AnimatePresence>
                {showPill && <StatusPill label={pillLabel} />}
              </AnimatePresence>

              {/* Bottom: transcript chunks not yet "consumed" by the note
                  generator, rendered dimmed so the user reads the panel
                  as "this transcript is being turned into the note above".
                  Wrapped in AnimatePresence so the `exit` opacity fade
                  actually fires when a chunk is consumed — without it
                  Framer Motion drops removed children instantly, which
                  pops the layout. `initial` is set to the same dimmed
                  opacity so each chunk doesn't flash at full opacity for
                  one frame on mount. */}
              <AnimatePresence initial={false}>
                {transcriptToNotePairs.slice(enhancedCount).map((pair, i) =>
                  pair.raw ? (
                    <m.div
                      key={`raw-${enhancedCount + i}`}
                      layout
                      initial={{ opacity: 0.3 }}
                      animate={{ opacity: 0.3 }}
                      exit={{ opacity: 0 }}
                      transition={{
                        layout: { duration: 0.7, ease: LAYOUT_EASE },
                        opacity: { duration: 0.4, ease: 'easeOut' },
                      }}
                    >
                      <TypographyP className="text-text-tertiary">{pair.raw}</TypographyP>
                    </m.div>
                  ) : null
                )}
              </AnimatePresence>
            </m.div>
          )}

          {showSignature &&
            activeTab === 'note' &&
            currentTask !== 'usedEvidence' &&
            // The tour's transcript view is its own tab, so the note's
            // sign-off has no business under the conversation.
            !(isGuidedTour && showTranscriptBody) && (
              <m.div
                className="mt-auto"
                initial={{ opacity: 0 }}
                animate={{
                  opacity:
                    isEditing && currentTask
                      ? 0.15
                      : stage === 'done' || isEditing
                        ? 1
                        : 0.4,
                }}
                transition={{ duration: 0.4 }}
              >
                <NoteSignature
                  firstName={formData.firstName}
                  lastName={formData.lastName}
                  specialtyName={localizedSpecialtyName}
                  clinicName={formData.clinicName}
                  displayLanguage={formData.displayLanguage}
                />
              </m.div>
            )}
        </div>

        {/* Tutorial disclaimer pinned to the bottom of the preview panel (under
            the content) while the Evidence tab is open. */}
        {isEditing && activeTab === 'evidence' && evidenceRequested && (
          <div className="flex shrink-0 items-start gap-2 border-t border-border px-6 py-3 text-[11px] leading-snug text-text-tertiary">
            <Info className="mt-0.5 size-3.5 shrink-0" />
            <span>
              <FormattedMessage
                defaultMessage="Tutorial example only. This is a simulated Evidence response illustrating the interface — not real clinical guidance for any patient."
                id="ulPPezxpOr"
                description="Disclaimer under the simulated Evidence response in the onboarding tutorial"
              />
            </span>
          </div>
        )}

        {/* The tour card runs its content to the card edge, so the last line
            fades out rather than being cut mid-glyph. */}
        {isGuidedTour && (
          <Fade
            side="bottom"
            background="hsl(var(--twc-surface))"
            className="inset-x-0 bottom-0 h-14 rounded-b-2xl"
          />
        )}
      </m.div>
    </div>
  );
}
